export type ConsentState = 'granted' | 'denied';

export const CONSENT_STORAGE_KEY = 'motiv8_cookie_consent';
export const OPEN_CONSENT_EVENT = 'motiv8:open-consent';

/**
 * IANA timezones for the EEA (EU + Iceland, Liechtenstein, Norway), the UK and
 * Switzerland, including the EU outermost regions where the GDPR also applies.
 *
 * This is an explicit allowlist rather than an `Europe/` prefix check, because
 * that prefix both over- and under-matches: it covers Moscow, Istanbul, Kyiv,
 * Belgrade and Minsk, which are outside the EEA, while missing Cyprus
 * (`Asia/Nicosia`), Ceuta, the Canaries and the French overseas departments.
 *
 * Entries must be the *canonical* zone id, since that is what browsers report
 * from resolvedOptions(). Deprecated aliases such as `Europe/Nicosia` never
 * match. Where a zone's canonical form varies by ICU version — Mariehamn,
 * Busingen, Vatican, San Marino, Guernsey, Jersey, Isle of Man — both the alias
 * and its target are listed so either resolution is caught.
 */
const EEA_TIME_ZONES = new Set([
    'Africa/Ceuta',
    'America/Cayenne',
    'America/Guadeloupe',
    'America/Martinique',
    'Arctic/Longyearbyen',
    'Asia/Famagusta',
    'Asia/Nicosia',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Madeira',
    'Atlantic/Reykjavik',
    'Europe/Amsterdam',
    'Europe/Athens',
    'Europe/Berlin',
    'Europe/Bratislava',
    'Europe/Brussels',
    'Europe/Bucharest',
    'Europe/Budapest',
    'Europe/Busingen',
    'Europe/Copenhagen',
    'Europe/Dublin',
    'Europe/Gibraltar',
    'Europe/Guernsey',
    'Europe/Helsinki',
    'Europe/Isle_of_Man',
    'Europe/Jersey',
    'Europe/Lisbon',
    'Europe/Ljubljana',
    'Europe/London',
    'Europe/Luxembourg',
    'Europe/Madrid',
    'Europe/Malta',
    'Europe/Mariehamn',
    'Europe/Monaco',
    'Europe/Oslo',
    'Europe/Paris',
    'Europe/Prague',
    'Europe/Riga',
    'Europe/Rome',
    'Europe/San_Marino',
    'Europe/Sofia',
    'Europe/Stockholm',
    'Europe/Tallinn',
    'Europe/Vaduz',
    'Europe/Vatican',
    'Europe/Vienna',
    'Europe/Vilnius',
    'Europe/Warsaw',
    'Europe/Zagreb',
    'Europe/Zurich',
    'Indian/Mayotte',
    'Indian/Reunion',
]);

/**
 * Best-effort check for whether the visitor is in a jurisdiction that requires
 * opt-in consent before analytics run.
 *
 * A static site has no server-side request, so there is no IP to geolocate.
 * The browser timezone is the closest signal available without shipping the
 * visitor's address to a third party before they have consented to anything.
 * It is a heuristic: a VPN or a traveller's laptop can misclassify in either
 * direction. Unknown timezones are treated as EEA so the safer path is default.
 */
export const isEeaVisitor = (): boolean => {
    if (typeof Intl === 'undefined') {
        return true;
    }

    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (!timeZone) {
            return true;
        }

        return EEA_TIME_ZONES.has(timeZone);
    } catch {
        return true;
    }
};

/**
 * Reads a key from localStorage, returning null if storage is unavailable.
 *
 * Safari in private browsing and browsers with site data blocked throw on
 * access rather than returning null, so every read and write is guarded.
 */
const readStorage = (key: string): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStorage = (key: string, value: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Storage unavailable. Consent stays in memory for this page view only,
        // which means the banner reappears on the next visit. That is the
        // correct failure mode: we cannot prove consent we did not persist.
    }
};

export const getConsent = (): ConsentState | null => {
    const stored = readStorage(CONSENT_STORAGE_KEY);

    return stored === 'granted' || stored === 'denied' ? stored : null;
};

export const setConsent = (state: ConsentState): void => {
    writeStorage(CONSENT_STORAGE_KEY, state);
};

/**
 * Asks the consent banner to reopen, so a visitor can change their mind.
 *
 * The GDPR requires withdrawing consent to be as easy as giving it. The footer
 * link dispatches this; the banner listens for it.
 */
export const requestConsentDialog = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
};
